import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { BRAND, IMG } from "@/lib/site";
import SEO from "@/components/SEO";

const VALUES = [
  {
    n: "01",
    title: "Diagnosis First",
    desc: "We refuse to specify a repair without proving the corrosion mechanism. Chloride, carbonation or something else, the fix follows the fact.",
  },
  {
    n: "02",
    title: "One Accountable Team",
    desc: "The engineer who surveyed the structure is the same one who signs off the repair. No handoffs, no finger-pointing.",
  },
  {
    n: "03",
    title: "Standards, Not Shortcuts",
    desc: "Repairs are executed to EN 1504 methodology. Bar cleaning to SA 2½. Cover behind the bar. Non-negotiable.",
  },
  {
    n: "04",
    title: "Honest Reporting",
    desc: "Our reports are readable, illustrated and costed. If a smaller repair will do, we'll say so.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About"
        description="Concrete Insight UK Ltd, a specialist diagnostic-first consultancy for reinforced concrete repair. EN 1504 methodology, one accountable team, from survey to final finish."
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// About · Concrete Insight UK</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            Concrete is our<br />material. Corrosion<br />is our problem.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            {BRAND.name} was founded to bring a diagnostic-first discipline to
            reinforced concrete repair. We work with asset owners, engineers
            and main contractors across the UK.
          </p>
        </div>
      </section>

      {/* Story with image */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative border border-zinc-900">
              <img src={IMG.rebarCloseup} alt="Corroded reinforcement exposed during breakout" loading="lazy" className="w-full h-auto block" />
              <div className="absolute top-3 left-3 font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-100 bg-zinc-950/80 px-2 py-1">
                REF · CI-0128 · REINFORCEMENT EXPOSED
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-zinc-200 space-y-6 text-zinc-700 text-base md:text-lg leading-relaxed">
            <div className="tech-label">// The story</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-zinc-900">
              Founded on a single principle: understand the mechanism.
            </h2>
            <p>
              Too many concrete repairs fail because the diagnosis was skipped.
              A cosmetic patch over an active corrosion cell will spall again,
              usually within a season or two.
            </p>
            <p>
              We built {BRAND.short} to do it properly: survey the element,
              test for the deterioration mechanism at play, chlorides,
              carbonation, sulphates, ASR or fire damage, quantify the
              extent, then execute a compliant repair. From car park decks
              and basement beams to office columns and slab soffits.
            </p>
            <p>
              Our clients are property owners who need a defensible repair, and
              engineers who need a specialist they can trust to interpret the
              data and get it right on site.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="tech-label">// The founder</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
              Behind the<br />business.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-zinc-200 space-y-6 text-zinc-700 text-base md:text-lg leading-relaxed">
            <div className="tech-label text-[#C2410C]">// Sam Preller, Director</div>
            <p>
              Concrete Insight was founded by <strong className="text-zinc-900">Sam Preller</strong>, a construction professional with a decade of experience spanning site management and structural building surveying.
            </p>
            <p>
              Sam progressed from trainee site manager through to running his own sites over six years, then moved into structural building surveying for a further four. That combination sits at the heart of the business: site experience to understand what a repair actually takes to deliver, alongside the diagnostic training to identify why concrete has failed in the first place.
            </p>
            <p>
              He founded {BRAND.short} to bring that combined perspective to specialist concrete repair, a market where investigation and delivery are too often handled by different teams that don&apos;t quite talk to each other.
            </p>
            <div className="pt-4 flex flex-wrap gap-3">
              <span className="px-3 py-1 border border-zinc-300 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-700">
                10 years in construction
              </span>
              <span className="px-3 py-1 border border-zinc-300 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-700">
                Structural Building Surveying
              </span>
              <span className="px-3 py-1 border border-zinc-300 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-700">
                SSSTS
              </span>
              <span className="px-3 py-1 border border-zinc-300 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-700">
                Confined Space Trained
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="bg-zinc-950 text-zinc-100 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="tech-label text-zinc-500">// Principles</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] text-zinc-50 max-w-3xl">
            How we work, and why it matters.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 border-t border-l border-zinc-800">
            {VALUES.map((v) => (
              <div key={v.n} className="p-8 md:p-12 border-r border-b border-zinc-800" data-testid={`value-${v.n}`}>
                <div className="font-mono text-xs text-[#D94515] tracking-[0.2em]">// {v.n}</div>
                <div className="font-display text-3xl md:text-4xl font-bold uppercase mt-6 leading-tight text-zinc-50">
                  {v.title}
                </div>
                <div className="rule-line bg-zinc-800 my-6" />
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capability strip */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="tech-label">// Capability</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
              What our team can<br />handle on site.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {[
              "Structural condition surveys with photographic defect registers",
              "Half-cell potential mapping and cover-meter analysis",
              "Chemical testing: chlorides, carbonation, sulphates and ASR",
              "Petrographic examination of concrete cores",
              "Post-fire structural assessment and residual strength testing",
              "Selective breakout with mechanical and hydro-demolition options",
              "Grit-blast cleaning of reinforcement to SA 2½",
              "Zinc-rich passivating primers to exposed bar",
              "R3 / R4 repair mortar reinstatement to EN 1504-3",
              "Galvanic sacrificial anode installation advice",
              "Anti-carbonation and crack-bridging coating specification",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 border-b border-zinc-200 pb-4">
                <Check size={18} className="mt-1 text-[#C2410C] shrink-0" strokeWidth={2.5} />
                <span className="text-zinc-800">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative border border-zinc-900 p-10 md:p-16 bg-zinc-50">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />
          <div className="tech-label">// Talk to us</div>
          <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
            Have a structure you&apos;re worried about?
          </h3>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="about-cta-enquire">
              Start an enquiry <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
